import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import BACKEND_URL from '../config';
import SEO from '../components/SEO';
import { useAuthModal } from '../context/AuthModalContext';

export default function Team() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const { openAuthModal } = useAuthModal();

  const [loading, setLoading] = useState(true);
 // const [orgInfo, setOrgInfo] = useState(null);
  const [mode, setMode] = useState('choose'); // choose | create | join
  const [teamName, setTeamName] = useState('');
  const [joinSlug, setJoinSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetch(`${BACKEND_URL}/org/mine/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.inOrg) {
          navigate('/team/dashboard');
        } else {
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));
  }, [user, navigate]);

  const handleCreate = async () => {
    if (!teamName.trim()) { setError('Team name is required'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/org/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name: teamName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to create team'); setSubmitting(false); return; }
      navigate('/team/dashboard');
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  const handleJoin = async () => {
    if (!joinSlug.trim()) { setError('Team link or code is required'); return; }
    setSubmitting(true); setError('');
    // Extract slug from full URL if pasted
    let slug = joinSlug.trim();
    const match = slug.match(/\/join\/([a-z0-9-]+)/i);
    if (match) slug = match[1];

    try {
      const res = await fetch(`${BACKEND_URL}/org/join/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, email: user.email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to join team'); setSubmitting(false); return; }
      navigate('/team/dashboard');
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  if (!user) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: c.secondary, marginBottom: '16px' }}>Sign in to access Teams</p>
        <button onClick={() => openAuthModal()} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '10px 24px', borderRadius: '7px', cursor: 'pointer', fontWeight: 700 }}>Sign in with Google</button>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: c.secondary }}>Loading...</p>
    </div>
  );

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>

      <SEO
        title="Teams"
        description="Create or join a Salesforce developer team on TrailCode. Track your team's coding progress together."
        url="https://trailcode.org/team"
        noIndex={true}
      />

      <div style={{ maxWidth: '480px', width: '100%' }}>
        <button onClick={() => navigate('/')} style={{ background: 'transparent', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '13px', marginBottom: '24px' }}>← Back to TrailCode</button>

        {mode === 'choose' && (
          <>
            <h1 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', letterSpacing: '-0.5px' }}>👥 Teams</h1>
            <p style={{ color: c.secondary, fontSize: '14px', marginBottom: '32px', lineHeight: 1.7 }}>
              Track your team's Salesforce skills together. Create a team for your company or join one with an invite link.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                onClick={() => setMode('create')}
                style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: c.shadow }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px' }}>🏢 Create a Team</p>
                <p style={{ fontSize: '12px', color: c.secondary, margin: 0 }}>Start a new team and invite your developers</p>
              </div>

              <div
                onClick={() => setMode('join')}
                style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: c.shadow }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px' }}>🔗 Join a Team</p>
                <p style={{ fontSize: '12px', color: c.secondary, margin: 0 }}>Use an invite link from your team admin</p>
              </div>
            </div>
          </>
        )}

        {mode === 'create' && (
          <>
            <button onClick={() => { setMode('choose'); setError(''); }} style={{ background: 'transparent', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '13px', marginBottom: '16px' }}>← Back</button>
            <h1 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '8px' }}>🏢 Create your team</h1>
            <p style={{ color: c.secondary, fontSize: '13px', marginBottom: '24px' }}>You'll become the team admin and can invite developers afterward.</p>

            <label style={{ fontSize: '12px', fontWeight: 600, color: c.secondary, display: 'block', marginBottom: '6px' }}>Team Name</label>
            <input
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              placeholder="e.g. Acme Consulting"
              style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: `1px solid ${c.border}`, background: c.card, color: c.text,
                fontSize: '14px', marginBottom: '16px', fontFamily: "'Inter', sans-serif",
              }}
            />

            {error && <p style={{ color: c.error, fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

            <button onClick={handleCreate} disabled={submitting} style={{
              width: '100%', background: c.accent, color: theme === 'dark' ? c.bg : '#fff',
              border: 'none', padding: '12px', borderRadius: '8px', cursor: submitting ? 'default' : 'pointer',
              fontSize: '14px', fontWeight: 700,
            }}>
              {submitting ? 'Creating...' : 'Create Team'}
            </button>
          </>
        )}

        {mode === 'join' && (
          <>
            <button onClick={() => { setMode('choose'); setError(''); }} style={{ background: 'transparent', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '13px', marginBottom: '16px' }}>← Back</button>
            <h1 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '8px' }}>🔗 Join a team</h1>
            <p style={{ color: c.secondary, fontSize: '13px', marginBottom: '24px' }}>Paste the invite link or code your admin shared with you.</p>

            <label style={{ fontSize: '12px', fontWeight: 600, color: c.secondary, display: 'block', marginBottom: '6px' }}>Invite Link or Code</label>
            <input
              value={joinSlug}
              onChange={e => setJoinSlug(e.target.value)}
              placeholder="trailcode.../join/acme-corp or acme-corp"
              style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: `1px solid ${c.border}`, background: c.card, color: c.text,
                fontSize: '14px', marginBottom: '16px', fontFamily: "'Inter', sans-serif",
              }}
            />

            {error && <p style={{ color: c.error, fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

            <button onClick={handleJoin} disabled={submitting} style={{
              width: '100%', background: c.accent, color: theme === 'dark' ? c.bg : '#fff',
              border: 'none', padding: '12px', borderRadius: '8px', cursor: submitting ? 'default' : 'pointer',
              fontSize: '14px', fontWeight: 700,
            }}>
              {submitting ? 'Joining...' : 'Join Team'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}